import { DistanceFilter, NumericalFilterType, NumericalFilterTypes } from '@paragon/contract-library';

import { formatLengthToArchitecturalString, parseImperialLengthToInches } from './imperialLengthTextUtilities';

/**
 * A utility function for creating a DistanceFilter from a given filter type and imperial string.
 */
function createFilter(type: NumericalFilterType, filteredValue: string): DistanceFilter | null {
  const value = parseImperialLengthToInches(filteredValue);
  if (!value) {
    return null;
  }

  return { type, value };
}

/**
 * Checks if two DistanceFilters are equal.
 */
export function distanceFiltersEqual(a: DistanceFilter | null, b: DistanceFilter | null) {
  if (a === null && b == null) {
    return true;
  }

  if (a == null || b == null) {
    return false;
  }

  return a.type === b.type && a.value === b.value;
}

/**
 * Attempts to parse a string representing a DistanceFilter.
 *
 * @returns A DistanceFilter if the string was successfully parsed, otherwise null.
 *
 * @example
 * parseDistanceFilter(`1' 9-3/8"`); // { type: '=', value: 21.375` }
 */
export function parseDistanceFilter(input: string): DistanceFilter | null {
  const trimmedInput = input.trim();
  for (const filterType of NumericalFilterTypes) {
    if (trimmedInput.startsWith(filterType)) {
      return createFilter(filterType, trimmedInput.replace(filterType, ''));
    }
  }

  return createFilter('=', trimmedInput);
}

/**
 * Converts a DistanceFilter to a human-readable string.
 *
 * @example
 * filterToString({ type: '<', value: 33.5 }); // < 2' 9-1/2"
 */
export function distanceFilterToString(filter: DistanceFilter | null) {
  if (!filter) {
    return '';
  }

  return `${filter.type} ${formatLengthToArchitecturalString(filter.value)}`;
}
